// Pantalla admin: configuración global de emails al profesor (SS7.7).
// 3 toggles + día del mes para resumen.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, Check, Mail } from 'lucide-react';

import { getConfigEmailsProfesor, putConfigEmailsProfesor } from '../../lib/adminApi';

export default function AdminConfigEmailsProfesor() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getConfigEmailsProfesor()
      .then((d) => { if (!cancelled) { setConfig(d); setOriginal(d); } })
      .catch((e) => { if (!cancelled) setLoadError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const dirty = config && original && JSON.stringify(config) !== JSON.stringify(original);

  const toggle = (k) => () => setConfig((c) => ({ ...c, [k]: !c[k] }));
  const setDia = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) setConfig((c) => ({ ...c, dia_resumen_mensual: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setOkMsg(null); setErrMsg(null);
    setSaving(true);
    try {
      const payload = {
        email_asignacion_clase: !!config.email_asignacion_clase,
        email_desasignacion_clase: !!config.email_desasignacion_clase,
        email_resumen_mensual: !!config.email_resumen_mensual,
        dia_resumen_mensual: config.dia_resumen_mensual,
      };
      const resp = await putConfigEmailsProfesor(payload);
      setConfig(resp);
      setOriginal(resp);
      setOkMsg(t('admin.config_emails.guardado_ok'));
      setTimeout(() => setOkMsg(null), 3500);
    } catch (e) {
      setErrMsg(e.message || t('admin.config_emails.error_guardar'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>;
  }
  if (loadError) {
    return <div className="admin-error"><AlertCircle size={20} /><span>{loadError.message}</span></div>;
  }
  if (!config) return null;

  return (
    <div className="admin-page admin-mi-cuenta">
      <header className="admin-page-head">
        <h1>
          <Mail size={20} strokeWidth={1.5} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          {t('admin.config_emails.titulo')}
        </h1>
        <p className="admin-page-sub">{t('admin.config_emails.sub')}</p>
      </header>

      <form onSubmit={submit} className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.config_emails.eventos_titulo')}</h2>
        <p className="admin-card-sub">{t('admin.config_emails.eventos_sub')}</p>

        <div className="admin-toggles">
          <label className="admin-toggle">
            <input type="checkbox" checked={!!config.email_asignacion_clase}
                   onChange={toggle('email_asignacion_clase')} />
            <span className="admin-toggle-label">
              <strong>{t('admin.config_emails.asignacion_titulo')}</strong>
              <small>{t('admin.config_emails.asignacion_desc')}</small>
            </span>
          </label>
          <label className="admin-toggle">
            <input type="checkbox" checked={!!config.email_desasignacion_clase}
                   onChange={toggle('email_desasignacion_clase')} />
            <span className="admin-toggle-label">
              <strong>{t('admin.config_emails.desasignacion_titulo')}</strong>
              <small>{t('admin.config_emails.desasignacion_desc')}</small>
            </span>
          </label>
          <label className="admin-toggle">
            <input type="checkbox" checked={!!config.email_resumen_mensual}
                   onChange={toggle('email_resumen_mensual')} />
            <span className="admin-toggle-label">
              <strong>{t('admin.config_emails.resumen_titulo')}</strong>
              <small>{t('admin.config_emails.resumen_desc')}</small>
            </span>
          </label>
        </div>

        <h2 className="admin-card-titulo" style={{ marginTop: 28 }}>{t('admin.config_emails.calendario_titulo')}</h2>
        <p className="admin-card-sub">{t('admin.config_emails.calendario_sub')}</p>
        <div className="admin-form-grid one-col">
          <div className="modal-campo">
            <label>{t('admin.config_emails.dia_label')}</label>
            <input
              type="number" min={1} max={28}
              value={config.dia_resumen_mensual || 1}
              onChange={setDia}
              disabled={!config.email_resumen_mensual}
              style={{ maxWidth: 120 }}
            />
            <small className="admin-hint">{t('admin.config_emails.dia_hint')}</small>
          </div>
        </div>

        {okMsg && (
          <div className="admin-msg-ok"><Check size={14} strokeWidth={1.75} /> {okMsg}</div>
        )}
        {errMsg && (
          <div className="modal-error-general"><AlertCircle size={14} strokeWidth={1.75} /> {errMsg}</div>
        )}

        <div className="admin-form-actions">
          <button type="submit" className="btn-primary" disabled={saving || !dirty}>
            {saving ? <><Loader2 size={14} className="spin" /> {t('admin.config_emails.guardando')}</> : t('admin.config_emails.guardar')}
          </button>
        </div>

        {original?.actualizado_at && (
          <p className="admin-page-sub" style={{ marginTop: 16 }}>
            <em>{t('admin.config_emails.ultima_act', { fecha: new Date(original.actualizado_at).toLocaleString() })}</em>
          </p>
        )}
      </form>
    </div>
  );
}
